import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { addDays, format, getDay, isBefore, startOfDay } from 'date-fns';
import { get, orderBy } from 'lodash';
import React from 'react';
import { createSelector } from 'reselect';
import { PriceChangeDirection, ResourceType } from '../../../common/PricePrgClient';
import { GuardedColDef, PriceCell } from '../../../common/datagrid';
import { AgHeaderComponent } from '../../../common/datagrid/components/AgHeaderComponent';
import { RootState } from '../../../store';
import {
    canSeeStockColumn,
    getPricelistRoot,
    getPricelistSettings,
    includes,
    notNull,
    roundTwoDecimals,
    startsWith,
    toDateString,
    toLowerCase
} from '../../../utils';
import { PricelistSettings } from '../../pricelist/pricelistTypes';
import { getPricelists } from '../../pricelist/redux/pricelistSelectors';
import { getProducts } from '../../product/redux/productSelectors';
import { getAppSettings } from '../../settings/redux/settingsSelectors';
import { getStocks } from '../../stock/redux/stockSelectors';
import { getStockAmount } from '../../stock/stockFunctions';
import { ProductStock } from '../../stock/stockTypes';
import { getCurrentUserPermissions } from '../../user/redux/userSelectors';
import { PriceRow, priceInitialColumns } from '../priceTypes';
import { savePriceComment } from './priceSlices';

export const getSelectedPricelistIds = (state: RootState) => state.price.selectedPricelistIds;
export const getPricelistPrices = (state: RootState) => state.price.prices;
export const getIsPriceTableLoading = (state: RootState) => state.price.ui.isLoading;
export const getPriceTable = (state: RootState) => state.price.ui.priceTable;
export const getPriceTableVisibleColumnKeys = createSelector(
    [(state: RootState) => state.price.ui.priceTable.columns],
    cols => cols.filter(c => !c.hide)
);
export const getIsChoosePricelistsDialogOpen = (state: RootState) =>
    state.price.ui.dialogs.isChoosePricelistsDialogOpen;
export const getCheckboxFilters = (state: RootState) => state.price.ui.checkboxFilters;
export const getSelectedPricelistDate = (state: RootState) => state.price.selectedDate;
export const getPriceSelectedWarehouse = (state: RootState) => state.price.selectedWarehouse;
export const getApplyCalculations = (state: RootState) => state.price.ui.applyCalculations;
export const getRootPricelistPeriods = (state: RootState) => state.price.rootPricelistPeriodDates;
const commonColumnProps: Partial<ColDef<PriceRow>> = {
    sortable: true,
    resizable: true,
    floatingFilter: true,
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: {
        suppressFilterButton: true
    }
};

export const getAgColumns = createSelector(
    [
        getSelectedPricelistIds,
        getPricelists,
        getPriceTableVisibleColumnKeys,
        getSelectedPricelistDate,
        getCurrentUserPermissions,
        getApplyCalculations,
        getPriceTable,
        getRootPricelistPeriods,
        getAppSettings
    ],
    (
        selectedPricelistIds,
        pricelists,
        priceTableColumnKeys,
        selectedPricelistDate,
        currentUserPermissions,
        applyCalculations,
        priceTable,
        rootPricelistPeriods,
        appSettings
    ) => {
        const earliestDateOfAllowedEditing = addDays(startOfDay(new Date()), 1);

        function* isDateAllowedForEdit(pricelistSettings: PricelistSettings | null, plId: number) {
            const selectedDate = new Date(selectedPricelistDate);

            if (isBefore(selectedDate, earliestDateOfAllowedEditing)) {
                yield `Not allowed to edit prices before ${toDateString(earliestDateOfAllowedEditing)}.`;
            }

            if (
                pricelistSettings?.hasWeekdayRestrictions &&
                !pricelistSettings.allowedWeekdays.includes(getDay(selectedDate))
            ) {
                yield `This pricelist forbids prices to be edited on ${format(selectedDate, 'EEEE')}s.`;
            }

            const pricelistRoot = getPricelistRoot(plId, pricelists);
            if (
                pricelistRoot?.pricelistId !== plId && //check if pricelist is root pricelist
                pricelistRoot?.pricelistId != null &&
                rootPricelistPeriods[pricelistRoot?.pricelistId]?.filter(p => {
                    const dateStr = toDateString(selectedDate);
                    return p.substring(0, 10) == dateStr;
                }).length == 0
            ) {
                yield `The parent pricelist does not have a pricelist period for ${selectedPricelistDate}`;
            }
        }

        const pricelistPermissions = currentUserPermissions.recordPermissions.find(
            x => x.resourceType === ResourceType.Pricelist
        );

        const hasPermissionToEdit = (plId: number) => {
            if (currentUserPermissions.isAdmin) {
                return true;
            }

            return (
                pricelistPermissions?.recordPermissions.some(x => x.recordId === plId && x.canUpdate) ?? false
            );
        };

        const cols = [
            ...priceInitialColumns.filter(colDef => {
                return canSeeStockColumn(colDef.field, currentUserPermissions);
            }),
            ...selectedPricelistIds
                .map(id => pricelists[id])
                .filter(notNull)
                .map(pl => {
                    const hasWritePermission = hasPermissionToEdit(pl.pricelistId);
                    const rootPricelist = getPricelistRoot(pl.pricelistId, pricelists);
                    const pricelistSettings = getPricelistSettings(pricelists[pl.pricelistId], pricelists);
                    const readonlyReasons: string[] = [
                        ...isDateAllowedForEdit(pricelistSettings, pl.pricelistId)
                    ];

                    if (!hasWritePermission) {
                        readonlyReasons.push("You don't have permission to edit prices on this pricelist.");
                    }

                    if (!applyCalculations && pl.forbidCalculationBypass) {
                        readonlyReasons.push(
                            'This pricelist forbids prices to be set without applying calculations.'
                        );
                    }

                    if (pricelistSettings?.isReadonly) {
                        readonlyReasons.push('This pricelist is readonly.');
                    }

                    const isEditable = readonlyReasons.length === 0;

                    return {
                        field: String(pl.pricelistId),
                        headerName: pl.name,
                        headerComponent: AgHeaderComponent,
                        headerComponentParams: { readonlyReasons },
                        cellDataType: 'text',
                        cellRenderer: ({ data: row, colDef: column }: ICellRendererParams<PriceRow>) => {
                            if (row == null || column == null) {
                                return React.Fragment;
                            }
                            const priceChangeDirection =
                                row.customerPriceChangeDirections[Number(column.field)];
                            const pricelistId = Number(column.field);
                            const priceToBeSaved = row.saves[pricelistId];
                            return PriceCell({
                                priceId: row.priceIds[pricelistId],
                                basePrice: row.basePrices[pricelistId],
                                price: priceToBeSaved ?? row[pricelistId],
                                priceChangeDirection,
                                updatedBy: row.updatedBys[pricelistId],
                                updatedDate: row.updatedDates[pricelistId],
                                commentOpts: {
                                    commentable: true,
                                    comment: row.comments[pricelistId],
                                    saveCommentActionCreator: (priceId, comment) =>
                                        savePriceComment.request({
                                            productId: row.productId,
                                            pricelistId,
                                            priceId,
                                            comment
                                        }),
                                    canWrite: isEditable
                                },
                                showTooltips: priceTable.showTooltips,
                                isSaving: priceToBeSaved != null,
                                defaultBgColor: isEditable ? undefined : 'rgba(244, 244, 244, .6)',
                                defaultColor: isEditable ? undefined : 'rgba(183,183,183, 0.3)',
                                styles:
                                    row.appliedCalculations.includes(pricelistId) || row[pricelistId] == null
                                        ? undefined
                                        : {
                                              textDecoration: 'underline 1px solid'
                                          },
                                exchangeRate: row.exchangeRates[pricelistId],
                                pricelistCurrency: rootPricelist?.currency,
                                defaultCurrency: appSettings.defaultCurrency
                            });
                        },
                        sortable: true,
                        editable: isEditable
                    } as GuardedColDef<PriceRow>;
                })
        ].map(column => ({
            ...column,
            ...commonColumnProps
        }));

        return priceTableColumnKeys
            .map(columnSetting => {
                const column = cols.find(col => col.field === columnSetting.key);
                if (column == null) {
                    return null;
                }
                return { ...column, width: columnSetting?.width ?? column.width };
            })
            .filter(notNull) as GuardedColDef<PriceRow>[];
    }
);

export const getPriceRows = createSelector(
    [
        getProducts,
        getSelectedPricelistIds,
        getPricelistPrices,
        getPriceTable,
        getCheckboxFilters,
        getStocks,
        getPriceSelectedWarehouse
    ],
    (
        products,
        selectedPricelistIds,
        prices,
        { filters, sorting },
        checkboxFilters,
        stocks,
        selectedWarehouse
    ) => {
        let productStocks: ProductStock = {};
        if (selectedWarehouse.date != null && selectedWarehouse.warehouse != null) {
            productStocks = stocks[selectedWarehouse.warehouse] ?? {};
        }

        function getStock(productId: number) {
            const productStockGrp = productStocks[productId];
            if (productStockGrp == null) {
                return null;
            }
            return getStockAmount(selectedWarehouse.date, productStockGrp);
        }

        const isFilteringPrices = selectedPricelistIds
            .map(id => filters[id])
            .some(f => f != null && f !== '');

        const priceRows = Object.values(products)
            .map(p => [p, getStock(p.productId)] as const)
            .filter(
                ([p, stock]) =>
                    (!selectedWarehouse.warehouse ||
                        p.warehouseIds?.includes(selectedWarehouse.warehouse ?? 0)) &&
                    startsWith(filters.productId, p.productId.toString()) &&
                    includes(filters.customProductAttribute1, p.customAttribute1) &&
                    includes(filters.unit, p.priceUnit) &&
                    includes(filters.productName, p.name) &&
                    includes(filters.purchaser, p.purchaser) &&
                    includes(filters.country, p.countryOfOrigin) &&
                    startsWith(filters.stock, stock?.toString()) &&
                    startsWith(filters.netWeight, p.netWeight.toString())
            )
            .map(([product, stock]) => {
                const priceRow: PriceRow = {
                    productId: product.productId,
                    productName: product.name,
                    customProductAttribute1: product.customAttribute1 ?? '',
                    unit: product.priceUnit ?? '',
                    country: product.countryOfOrigin ?? '',
                    purchaser: product.purchaser ?? '',
                    customerPriceChangeDirections: {},
                    stock: stock ?? null,
                    netWeight: product.netWeight,
                    updatedBys: {},
                    updatedDates: {},
                    priceIds: {},
                    comments: {},
                    saves: {},
                    agreementPriceIds: [],
                    appliedCalculations: [],
                    boxesPerPallet: product.boxesPerPallet ?? 0,
                    basePrices: {},
                    exchangeRates: {}
                };
                for (const pricelistId of selectedPricelistIds) {
                    const custPrice = prices[`${product.productId}-${pricelistId}`];
                    if (custPrice != null) {
                        priceRow[pricelistId] = roundTwoDecimals(custPrice.price);
                        priceRow.customerPriceChangeDirections[pricelistId] = custPrice.priceChangeDirection;

                        if (custPrice.updatedBy != null) {
                            priceRow.updatedBys[pricelistId] = custPrice.updatedBy;
                        }
                        if (custPrice.updatedDate != null) {
                            priceRow.updatedDates[pricelistId] = new Date(custPrice.updatedDate);
                        }
                        if (custPrice.priceId != null) {
                            priceRow.priceIds[pricelistId] = custPrice.priceId;
                        }
                        if (custPrice.comment != null) {
                            priceRow.comments[pricelistId] = custPrice.comment;
                        }
                        if (custPrice.priceToBeSaved != null) {
                            priceRow.saves[pricelistId] = custPrice.priceToBeSaved;
                        }
                        if (custPrice.basePrice != null) {
                            priceRow.basePrices[pricelistId] = custPrice.basePrice;
                        }

                        priceRow.agreementPriceIds.push(...custPrice.agreementPriceIds);

                        if (custPrice.appliedCalculations) {
                            priceRow.appliedCalculations.push(pricelistId);
                        }
                        if (custPrice.exchangeRate != null) {
                            priceRow.exchangeRates[pricelistId] = custPrice.exchangeRate;
                        }
                    }
                }

                return priceRow;
            })
            .filter(priceRow => {
                if (checkboxFilters.showCommentsOnly && Object.keys(priceRow.comments).length === 0) {
                    return false;
                }
                if (checkboxFilters.showNewArticlesOnly) {
                    return selectedPricelistIds
                        .map(id => priceRow.customerPriceChangeDirections[id])
                        .some(pc => pc != null && pc === PriceChangeDirection.NewArticle);
                }
                if (
                    selectedPricelistIds.length === 0 ||
                    (checkboxFilters.showAllArticles && !isFilteringPrices)
                ) {
                    return true;
                }

                if (!isFilteringPrices) {
                    return selectedPricelistIds.map(id => priceRow[id]).some(pr => pr != null);
                }

                return selectedPricelistIds
                    .map(id => [filters[id] as string, priceRow[id]] as const)
                    .every(([filter, price]) => startsWith(filter, price?.toString()));
            });

        const nullOrderPlacement = sorting.direction === 'asc' ? Infinity : -Infinity;

        const col = !isNaN(Number(sorting.column)) ? `updatedDates.${sorting.column}` : sorting.column;

        return orderBy(priceRows, [o => toLowerCase(get(o, col)) || nullOrderPlacement], sorting.direction);
    }
);
